import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ViewJobAction } from "../../../../Redux/action/ViewJobAction";
import ebLogo from "../../../../images/ebench_logo.png";
import { ApplyJobAction } from "../../../../Redux/action/ApplyJobAction";

// MUI Components
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

// Icons
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ShareIcon from "@mui/icons-material/Share";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import Loader from "../../../Common/Loader/loader";
import BackButton from "../../../Common/BackButton/BackButton";

const PRIMARY = "#286a94";

export default function ViewJob() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state.ViewJob.result);
  const loadingData = useSelector((state) => state.ViewJob.loading);
  const applyLoading = useSelector((state) => state.ApplyJob.loading);

  let { id } = useParams();
  const paramsId = new URLSearchParams(location.search).get("jobId");
  const jobId = paramsId || id;

  const appliedStatus = new URLSearchParams(location.search).get(
    "fromApplicationStatus"
  );

  const [saved, setSaved] = useState(false);
  const [shareMenu, setShareMenu] = useState(false);
  const shareRef = useRef(null);
  useEffect(() => {
    dispatch(ViewJobAction(jobId));
  }, []);

  const handleApply = () => {
    dispatch(
      ApplyJobAction({
        candidateId: [userResult?.userid],
        jobId,
      })
    ).then(() => navigate("/new_job"));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        shareMenu &&
        shareRef.current &&
        !shareRef.current.contains(event.target)
      ) {
        setShareMenu(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [shareMenu]);

  if (loadingData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="75vh"
      >
        <Loader />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ maxWidth: "1250px", mx: "auto", px: 2, py: 3 }}>
        <BackButton />

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* ---------------- LEFT SIDE ---------------- */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: "16px" }}>
              {/* ------ Header with Apply Button ------ */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h4" fontWeight={700} color={PRIMARY}>
                  {userInfo?.jobTitle}
                </Typography>

                {!appliedStatus && (
                  <Button
                    variant="contained"
                    sx={{
                      background: PRIMARY,
                      borderRadius: "25px",
                      px: 3,
                      "&:hover": { background: "#1b4e6b" },
                    }}
                    onClick={handleApply}
                  >
                    {applyLoading ? "Applying..." : "Apply Now"}
                  </Button>
                )}
              </Box>

              {/* Meta actions */}
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                {/* <Chip
                  icon={<BookmarkIcon color="#286a94" />}
                  label={saved ? "Saved" : "Save Job"}
                  onClick={() => setSaved(!saved)}
                  sx={{
                    bgcolor: "#e6f2ff",
                    color: PRIMARY,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                /> */}

                <Chip
                  icon={
                    <ShareIcon color="#286a94" style={{ fontSize: "16px" }} />
                  }
                  label="Share Job"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShareMenu((prev) => !prev);
                  }}
                  sx={{
                    bgcolor: "#edf7ff",
                    color: PRIMARY,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                />
              </Box>

              <Box ref={shareRef}>
                {shareMenu && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      mt: 2.5,
                      bgcolor: "rgba(255,255,255,0.75)",
                      backdropFilter: "blur(10px)",
                      borderRadius: "50px",
                      p: 1.2,
                      boxShadow: "0 4px 18px rgba(0,0,0,0.08)",
                      width: "fit-content",
                      alignItems: "center",
                      animation: "fadeIn 0.25s ease-in-out",
                    }}
                  >
                    <Tooltip title="Share on WhatsApp">
                      <Box
                        onClick={() =>
                          window.open(
                            `https://wa.me/?text=${window.location.href}`,
                            "_blank"
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "#25D366",
                          display: "grid",
                          placeItems: "center",
                          transition: "0.25s",
                          "&:hover": {
                            transform: "scale(1.15)",
                            boxShadow: "0 0 12px #25D366",
                          },
                        }}
                      >
                        <WhatsAppIcon sx={{ color: "#fff" }} />
                      </Box>
                    </Tooltip>

                    <Tooltip title="Share on LinkedIn">
                      <Box
                        onClick={() =>
                          window.open(
                            `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`,
                            "_blank"
                          )
                        }
                        sx={{
                          cursor: "pointer",
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "#0A66C2",
                          display: "grid",
                          placeItems: "center",
                          transition: "0.25s",
                          "&:hover": {
                            transform: "scale(1.15)",
                            boxShadow: "0 0 12px #0A66C2",
                          },
                        }}
                      >
                        <LinkedInIcon sx={{ color: "#fff" }} />
                      </Box>
                    </Tooltip>

                    <Tooltip title="Copy Link">
                      <Box
                        onClick={() =>
                          navigator.clipboard.writeText(window.location.href)
                        }
                        sx={{
                          cursor: "pointer",
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          bgcolor: "#286a94",
                          display: "grid",
                          placeItems: "center",
                          transition: "0.25s",
                          "&:hover": {
                            transform: "scale(1.15)",
                            boxShadow: "0 0 12px #286a94",
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ color: "#fff" }} />
                      </Box>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* ---------------- FIELDS RESTORED ---------------- */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    mb={2}
                    fontWeight={600}
                    fontSize="16px"
                    color={PRIMARY}
                  >
                    Job Details
                  </Typography>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Job ID:</strong> {userInfo?.jobUuid}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Job Posted on:</strong> {userInfo?.postedDate}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography color="#444">
                    <strong>Work Type:</strong> {userInfo?.workType}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography color="#444">
                    <strong>Job Type:</strong>{" "}
                    {userInfo?.employmentType === "FULL_TIME"
                      ? "Full Time"
                      : userInfo?.employmentType === "PART_TIME"
                      ? "Part Time"
                      : "Flexible"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography color="#444">
                    <strong>Experience:</strong>{" "}
                    {userInfo?.minimumExperience?.includes("0")
                      ? `Fresher - ${userInfo?.maximumExperience}`
                      : `${userInfo?.minimumExperience} - ${userInfo?.maximumExperience}`}
                  </Typography>
                </Grid>

               
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Working Week Days:</strong>{" "}
                    {userInfo?.workingWeekDays?.join(", ")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Working Days:</strong> {userInfo?.workingDays}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography>
                    <strong>Time Zone:</strong> {userInfo?.timeZone}
                  </Typography>
                </Grid>
                 <Grid item xs={12} sm={12}>
                  <Typography color="#444">
                    <strong>Required Skills:</strong>{" "}
                    {userInfo?.requiredSkills?.join(", ")}
                  </Typography>
                </Grid>


                {/* <Grid item xs={12}>
                  <Typography>
                    <strong>Vendor ID:</strong> {userInfo?.vendorEmpId}
                  </Typography>
                </Grid> */}
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography fontWeight={700} sx={{ mb: 1 }} color={"#286a94"}>
                Job Description:
              </Typography>

              <Typography sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}>
                {userInfo?.jobDescription}
              </Typography>
            </Paper>
          </Grid>

          {/* ---------------- RIGHT SIDEBAR ---------------- */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 4,
                borderRadius: "16px",
                border: "1px solid #dbe6f2",
                textAlign: "center",
              }}
            >
              <img src={ebLogo} alt="logo" height="60" />
              <Typography sx={{ mt: 2, fontWeight: 700, fontSize: "18px" }}>
                eBench Pvt. Ltd.
              </Typography>

              <Typography sx={{ color: "#576574", mt: 1 }}>
                AI-powered hiring platform connecting talent with the right
                opportunity faster.
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={600}>Why Apply With Us?</Typography>

              <Box sx={{ mt: 1, color: "#444", lineHeight: 1.7 }}>
                • ⚡ Faster Hiring • 🎯 Matching Accuracy • 🌍 Remote Friendly •
                🔐 Verified Employers
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={600}>Follow Us</Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: 1,
                }}
              >
                <IconButton>
                  <LinkedInIcon sx={{ color: PRIMARY }} />
                </IconButton>
                <IconButton>
                  <WhatsAppIcon sx={{ color: "green" }} />
                </IconButton>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
